import * as React from "react";
import { Box, Typography, Card, Grid } from "@mui/material";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import Person4Icon from "@mui/icons-material/Person4";
import SafetyDividerIcon from "@mui/icons-material/SafetyDivider";
import PostAddIcon from "@mui/icons-material/PostAdd";
import CurrencyPoundIcon from "@mui/icons-material/CurrencyPound";
import DonutSmallIcon from "@mui/icons-material/DonutSmall";
import ChromeReaderModeIcon from "@mui/icons-material/ChromeReaderMode";
import { tl } from "../../components/translate";
import Can from "../Policy/Can";
import mixpanelAnalytics from "../../mixpanel-analytics/mixpanelAnalytics";
import EVENT from "../../mixpanel-analytics/event";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";

// @ts-expect-error - css.
import styles from "./Reports.module.css";
import { reportCardStyle, reportsCardContainerStyle } from "./ReportTypesList";

const ReportsFinancial = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const rc = useCurrentResourceCentre();

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ ...reportsCardContainerStyle }}>
        <Can policyAccessKey="report:salesReport">
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => {
              mixpanelAnalytics.track(EVENT.REPORT_GO_TO_FINANCIAL_SALES, {
                rcId: rc.id,
                rcName: rc.name
              });
              return dispatch(push("/reports/financial/salesReport"));
            }}
            data-testmation="salesReportCard"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography>
                    <Box component="span" fontWeight="700" fontSize="24px">
                      {tl("reports.salesReport")}
                    </Box>
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <ChromeReaderModeIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>
        <Can policyAccessKey="report:feesAndReferralReport">
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => {
              mixpanelAnalytics.track(EVENT.REPORT_GO_TO_FINANCIAL_AND_REFERRAL, {
                rcId: rc.id,
                rcName: rc.name
              });
              return dispatch(push("/reports/financial/commissionReport"));
            }}
            data-testmation="commissionReport"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px" component="span">
                    SP and Referral Commission Report
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <MonetizationOnIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>
        <Can policyAccessKey="report:salesByServiceReport">
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => {
              mixpanelAnalytics.track(EVENT.REPORT_GO_TO_FINANCIAL_SALES_BY_SERVICE, {
                rcId: rc.id
              });
              return dispatch(push("/reports/financial/salesByServiceReport"));
            }}
            data-testmation="salesByServiceReportCard"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px" component="span">
                    {tl("reports.salesByServiceReport")}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <PostAddIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>
        <Can policyAccessKey="report:dueReport">
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => {
              mixpanelAnalytics.track(EVENT.REPORT_GO_TO_FINANCIAL_DUE, {
                rcId: rc.id,
                rcName: rc.name
              });
              return dispatch(push("/reports/financial/dueReport"));
            }}
            data-testmation="dueReport"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px" component="span">
                    {tl("reports.dueReport")}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <AssignmentReturnIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>
        <Can policyAccessKey="report:receiptReport">
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => {
              mixpanelAnalytics.track(EVENT.REPORT_GO_TO_FINANCIAL_RECEIPT, {
                rcId: rc.id,
                rcName: rc.name
              });
              return dispatch(push("/reports/financial/receiptReport"));
            }}
            data-testmation="receiptReport"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px" component="span">
                    {tl("reports.receiptReport")}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <ReceiptIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>
        <Can policyAccessKey="report:clientLedgerReport">
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => {
              mixpanelAnalytics.track(EVENT.REPORT_GO_TO_FINANCIAL_CLIENT_LEDGER, {
                rcId: rc.id,
                rcName: rc.name
              });
              return dispatch(push("/reports/financial/clientLedger"));
            }}
            data-testmation="clientLedgerReport"
          >
            <Grid container>
              <Grid size={8}>
                <Typography fontWeight="700" fontSize="24px">
                  Client Ledger Report
                </Typography>
              </Grid>
              <Grid size={4}>
                <CurrencyPoundIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>
        <Can policyAccessKey="report:serviceProviderChargesReport">
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => {
              mixpanelAnalytics.track(EVENT.REPORT_GO_TO_FINANCIAL_SERVICE_PROVIDER_CHARGES, {
                rcId: rc.id
              });
              return dispatch(push("/reports/financial/serviceProviderChargesReport"));
            }}
            data-testmation="serviceProviderChargesReportCard"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography>
                    <Box component="span" fontWeight="700" fontSize="24px">
                      {tl("reports.serviceProviderChargesReport")}
                    </Box>
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <DonutSmallIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>
        <Can policyAccessKey="report:ipdBillsReport">
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => {
              mixpanelAnalytics.track(EVENT.REPORT_GO_TO_IPD_BILLS_REPORT, {
                rcId: rc.id
              });
              return dispatch(push("/reports/financial/ipdBillsReport"));
            }}
            data-testmation="ipdBillsReport"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px">
                    {tl("reports.ipdBillsReport")}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <ReceiptLongIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>
        <Can policyAccessKey="report:salesByServiceProviderReport">
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => {
              mixpanelAnalytics.track(EVENT.REPORT_GO_TO_SALES_BY_SERVICE_PROVIDER_REPORT, {
                rcId: rc.id
              });
              return dispatch(push("/reports/financial/salesByServiceProviderReport"));
            }}
            data-testmation="salesByServiceProviderReportCard"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px" component="span">
                    {tl("reports.salesByServiceProviderReport")}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <ReceiptLongIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>
        <Can policyAccessKey="report:billingPackageReport">
          <Card
            sx={{ ...reportCardStyle }}
            onClick={() => {
              mixpanelAnalytics.track(EVENT.REPORT_GO_TO_BILLING_PACKAGE_REPORT, {
                rcId: rc.id
              });
              return dispatch(push("/reports/financial/billingPackage"));
            }}
            data-testmation="salesByServiceProviderReportCard"
          >
            <Grid container>
              <Grid size={8}>
                <Box height="100%" width="100%">
                  <Typography fontWeight="700" fontSize="24px" component="span">
                    Billing Package Report
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <ReceiptLongIcon className={styles.reportCardChartIcon} />
              </Grid>
            </Grid>
          </Card>
        </Can>
        <Card
          sx={{ ...reportCardStyle }}
          onClick={() => {
            mixpanelAnalytics.track(EVENT.REPORT_GO_TO_USER_WISE_COLLECTION, {
              rcId: rc.id
            });
            return dispatch(push("/reports/financial/userWiseCollection"));
          }}
        >
          <Grid container>
            <Grid size={8}>
              <Box height="100%" width="100%">
                <Typography fontWeight="700" fontSize="24px" component="span">
                  User Wise Collection Report
                </Typography>
              </Box>
            </Grid>
            <Grid size={4}>
              <Person4Icon className={styles.reportCardChartIcon} />
            </Grid>
          </Grid>
        </Card>
        <Card
          sx={{ ...reportCardStyle }}
          onClick={() => {
            mixpanelAnalytics.track(EVENT.REPORT_GO_TO_DEPARTMENT_WISE_COLLECTION, {
              rcId: rc.id
            });
            return dispatch(push("/reports/financial/departmentWiseCollection"));
          }}
        >
          <Grid container>
            <Grid size={8}>
              <Box height="100%" width="100%">
                <Typography fontWeight="700" fontSize="24px" component="span">
                  Department Wise Collection Report
                </Typography>
              </Box>
            </Grid>
            <Grid size={4}>
              <SafetyDividerIcon className={styles.reportCardChartIcon} />
            </Grid>
          </Grid>
        </Card>
      </Box>
    </Box>
  );
};

export default ReportsFinancial;
